import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { MaterialReactTable } from 'material-react-table';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import { createItem, getAllItems, updateItem, deleteItem } from '../../api/routes'
// import { data, states } from './makeData';

const VendingMachineItems = ({user_id}) => {
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [itemData, setItemData] = useState(() => []);
  const [validationErrors, setValidationErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const normalizeText = (value) => (typeof value === 'string' ? value.trim() : '');
  const ITEM_NAME_REGEX = /^[A-Za-z0-9][A-Za-z0-9 _.,()/-]{0,99}$/;
  const CODE_REGEX = /^[A-Za-z0-9_-]{0,50}$/;

  const extractMetadata = (rawMetadata) => {
    if (rawMetadata && typeof rawMetadata === 'object') {
      return {
        description: normalizeText(rawMetadata.description),
        user_id: rawMetadata.user_id || null,
      };
    }
    if (typeof rawMetadata === 'string') {
      return { description: normalizeText(rawMetadata), user_id: null };
    }
    return { description: '', user_id: null };
  };

  const validateItemValues = (values) => {
    const errors = {};
    const itemName = normalizeText(values.item_name);
    const metadata = normalizeText(values.metadata);
    const code = normalizeText(values.code);
    const price = Number(values.item_price);
    const tax = Number(values.tax);

    if (!itemName || !ITEM_NAME_REGEX.test(itemName)) {
      errors.item_name = 'Use letters/numbers and only _ . , ( ) / -';
    }
    if (!metadata) {
      errors.metadata = 'Description is required';
    }
    if (!Number.isFinite(price) || price < 0) {
      errors.item_price = 'Price must be 0 or greater';
    }
    if (!Number.isFinite(tax) || tax < 0) {
      errors.tax = 'Tax must be 0 or greater';
    }
    if (code && !CODE_REGEX.test(code)) {
      errors.code = 'Code allows only letters, numbers, _ and -';
    }
    return errors;
  };

  const updateItemResponseData = (data) => {
    const normalized = data.map((item) => {
      const metadata = extractMetadata(item.metadata);
      const nextItem = {
        ...item,
        item_name: normalizeText(item.item_name),
        code: normalizeText(item.code),
        created_at: item.created_at ? new Date(item.created_at).toLocaleString() : '',
        updated_at: item.updated_at ? new Date(item.updated_at).toLocaleString() : '',
        metadata: metadata.description,
        user_id: metadata.user_id,
      };

      if (item.expiry_date) {
        const expiryDate = new Date(item.expiry_date);
        if (!Number.isNaN(expiryDate.getTime())) {
          nextItem.expiry_date = expiryDate.toISOString().slice(0, 10);
        }
      }
      return nextItem;
    });
    return normalized.filter((item) => item.user_id === user_id);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const items = await getAllItems();
      console.log({ items });
      setItemData(updateItemResponseData(items));
      // const response = await getAllVM();

      // setTableData(updateVMResponseData(response, users));
      setLoading(false);
    } catch (error) {
      console.log("error", error);
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
    // console.log({admins});
  }, []);

  const handleCreateNewRow = async (values) => {
    const formErrors = validateItemValues(values);
    if (Object.keys(formErrors).length) {
      setValidationErrors(formErrors);
      return false;
    }
    setValidationErrors({});
    delete values["guid"];
    delete values["created_at"];
    delete values["updated_at"];
    values["item_name"] = normalizeText(values["item_name"]);
    values["item_price"] = parseInt(values["item_price"], 10);
    values["tax"] = parseFloat(values["tax"]);
    values["code"] = normalizeText(values["code"]) || null;
    values["expiry_date"] = values["expiry_date"] || null;
    values["metadata"] = { description: normalizeText(values["metadata"]), user_id: user_id };
    await createItem(values);
    await fetchData();
    return true;
  };

  const handleSaveRowEdits = async ({ exitEditingMode, row, values }) => {
    const formErrors = validateItemValues(values);
    if (Object.keys(formErrors).length) {
      setValidationErrors(formErrors);
      return;
    }
    setValidationErrors({});
    itemData[row.index] = values;
    console.log("handleSaveRowEdits -----", values);
    setItemData([...itemData]);
    await updateItem(values.guid, {
      "item_name": normalizeText(values.item_name),
      "item_price": Number(values.item_price),
      "tax": Number(values.tax),
      "code": normalizeText(values.code) || null,
      "expiry_date": values.expiry_date || null,
      "metadata": { "description": normalizeText(values.metadata) },
    });
    fetchData();
    exitEditingMode(); //required to exit editing mode and close modal
  };

  const handleCancelRowEdits = () => {
    setValidationErrors({});
  };

  const handleDeleteRow = useCallback(
    async (row) => {
      if (
        // eslint-disable-next-line no-restricted-globals
        !confirm(`Are you sure you want to delete ${row.getValue('item_name')}`)
      ) {
        return;
      }
      //send api delete request here, then refetch or update local table data for re-render
      await deleteItem(row["original"]["guid"])
      itemData.splice(row.index, 1);
      setItemData([...itemData]);
    },
    [itemData],
  );

  // const getCommonEditTextFieldProps = useCallback(
  //   (cell) => {
  //     return {
  //       error: !!validationErrors[cell.id],
  //       helperText: validationErrors[cell.id],
  //       onBlur: (event) => {
  //         const isValid =
  //           cell.column.id === 'email'
  //             ? validateEmail(event.target.value)
  //             : cell.column.id === 'age'
  //             ? validateAge(+event.target.value)
  //             : validateRequired(event.target.value);
  //         if (!isValid) {
  //           //set validation error for cell if invalid
  //           setValidationErrors({
  //             ...validationErrors,
  //             [cell.id]: `${cell.column.columnDef.header} is required`,
  //           });
  //         } else {
  //           //remove validation error for cell if valid
  //           delete validationErrors[cell.id];
  //           setValidationErrors({
  //             ...validationErrors,
  //           });
  //         }
  //       },
  //     };
  //   },
  //   [validationErrors],
  // );

  const columns = useMemo(
    () => [
      {
        accessorKey: 'guid',
        header: 'ID',
        enableColumnOrdering: false,
        enableEditing: false, //disable editing on this column
        enableSorting: false,
        size: 80,
      },
      {
        accessorKey: 'item_name',
        header: 'Item Name',
        size: 140,
        Cell: ({ cell }) => String(cell.getValue() || ''),
        muiTableBodyCellEditTextFieldProps: ({ cell }) => ({
          // ...getCommonEditTextFieldProps(cell),
        }),
      },
      {
        accessorKey: 'item_price',
        header: 'Price',
        size: 80,
        muiEditTextFieldProps: {
          // ...getCommonEditTextFieldProps(cell),
          type: 'number',
          required: true,
          error: !!validationErrors?.item_price,
          helperText: validationErrors?.item_price,
          //remove any previous validation errors when user focuses on the input
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              item_price: undefined,
            }),
        },
      },
      {
        accessorKey: 'tax',
        header: 'Tax',
        size: 80,
        muiTableBodyCellEditTextFieldProps: ({ cell }) => ({
          // ...getCommonEditTextFieldProps(cell),
          type: 'number',
        }),
      },
      {
        accessorKey: 'code',
        header: 'Code',
        size: 120,
        Cell: ({ cell }) => String(cell.getValue() || ''),
        muiTableBodyCellEditTextFieldProps: ({ cell }) => ({
          // ...getCommonEditTextFieldProps(cell),
        }),
      },
      {
        accessorKey: 'expiry_date',
        header: 'Expiry Date',
        size: 120,
        muiTableBodyCellEditTextFieldProps: {
          type: 'date',
          InputLabelProps: { shrink: true },
        },
      },
      {
        accessorKey: 'metadata',
        header: 'Item Description',
        size: 140,
        Cell: ({ cell }) => String(cell.getValue() || ''),
        muiTableBodyCellEditTextFieldProps: ({ cell }) => ({
          // ...getCommonEditTextFieldProps(cell),
        }),
      },
      {
        accessorKey: 'created_at',
        header: 'Created At',
        size: 40,
        enableColumnOrdering: false,
        enableEditing: false, 
        enableSorting: false,
      },
      {
        accessorKey: 'updated_at',
        header: 'Updated At',
        size: 40,
        enableColumnOrdering: false,
        enableEditing: false, 
        enableSorting: false,
      },
    ],
    // [getCommonEditTextFieldProps],
  );

  return (
    <>
      <MaterialReactTable
        displayColumnDefOptions={{
          'mrt-row-actions': {
            muiTableHeadCellProps: {
              align: 'center',
            },
            size: 120,
          },
        }}
        columns={columns}
        data={itemData}
        editingMode="modal" //default
        enableColumnOrdering
        enableEditing
        onEditingRowSave={handleSaveRowEdits}
        onEditingRowCancel={handleCancelRowEdits}
        renderRowActions={({ row, table }) => (
          <Box sx={{ display: 'flex', gap: '1rem' }}>
            <Tooltip arrow placement="left" title="Edit">
              <IconButton onClick={() => table.setEditingRow(row)}>
                <Edit />
              </IconButton>
            </Tooltip>
            <Tooltip arrow placement="right" title="Delete">
              <IconButton color="error" onClick={() => handleDeleteRow(row)}>
                <Delete />
              </IconButton>
            </Tooltip>
          </Box>
        )}
        renderTopToolbarCustomActions={() => (
          <Button
            color="secondary"
            onClick={() => setCreateModalOpen(true)}
            variant="contained"
          >
            Create New Item
          </Button>
        )}
      />
      <CreateNewAccountModal
        columns={columns}
        open={createModalOpen}
        validationErrors={validationErrors}
        onClose={() => setCreateModalOpen(false)}
        onSubmit={handleCreateNewRow}
      />
    </>
  );
};

//example of creating a mui dialog modal for creating new rows
export const CreateNewAccountModal = ({ open, columns, onClose, onSubmit, validationErrors }) => {
  const [values, setValues] = useState(() =>
    columns.reduce((acc, column) => {
      acc[column.accessorKey ?? ''] = '';
      return acc;
    }, {}),
  );

  const handleSubmit = () => {
    onSubmit(values).then((created) => {
      if (created) {
        onClose();
      }
    });
  };

  return (
    <Dialog open={open}>
      <DialogTitle textAlign="center">Create New Account</DialogTitle>
      <DialogContent>
        <form onSubmit={(e) => e.preventDefault()}>
          <Stack
            sx={{
              width: '100%',
              minWidth: { xs: '300px', sm: '360px', md: '400px' },
              gap: '1.5rem',
            }}
          >
            {columns.map((column) => (
              (column.accessorKey === "item_name" || column.accessorKey === "metadata" || column.accessorKey === "item_price" || column.accessorKey === "tax" || column.accessorKey === "code" || column.accessorKey === "expiry_date") && <TextField
                key={column.accessorKey}
                label={column.header}
                name={column.accessorKey}
                onChange={(e) =>
                  setValues({ ...values, [e.target.name]: e.target.value })
                }
                value={values[column.accessorKey] ?? ''}
                error={Boolean(validationErrors?.[column.accessorKey])}
                helperText={validationErrors?.[column.accessorKey] || ''}
                type={column.accessorKey === "expiry_date" ? "date" : "text"}
                InputLabelProps={column.accessorKey === "expiry_date" ? { shrink: true } : undefined}
              />
            ))}
          </Stack>
        </form>
      </DialogContent>
      <DialogActions sx={{ p: '1.25rem' }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button color="secondary" onClick={handleSubmit} variant="contained">
          Create New Item
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// const validateRequired = (value) => !!value.length;
// const validatePrice = (price) =>price
//     .match(
//       /^[0-9]/,
//     );

// function validateItem(item) {
//   return {
//     price: validatePrice(item.item_price)
//   };
// }

export default VendingMachineItems;
